import React from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Renders the Verified / Possible Harvest Correction analytical signal.
 * - status='verified'  → strong amber card, High confidence wording, full
 *                        recommended-actions list ("review historical sample
 *                        methodology", etc).
 * - status='possible'  → softer yellow card, Moderate confidence wording,
 *                        "continue normal sampling / seek confirmation"
 *                        recommended actions.
 *
 * Read-only analytical signal — never mutates any underlying record.
 */
export default function VerifiedHarvestCorrectionCard({ signal }) {
  if (!signal || (signal.status !== 'verified' && signal.status !== 'possible') || !signal.trigger) {
    return null;
  }

  const t = signal.trigger;
  const confirmations = signal.confirmations || [];
  const isVerified = signal.status === 'verified';

  const palette = isVerified
    ? {
        border: 'border-amber-400 dark:border-amber-600',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        accent: 'text-amber-900 dark:text-amber-100',
        sub: 'text-amber-700 dark:text-amber-300',
        divider: 'border-amber-300 dark:border-amber-700',
      }
    : {
        border: 'border-yellow-300 dark:border-yellow-700',
        bg: 'bg-yellow-50 dark:bg-yellow-900/15',
        accent: 'text-yellow-900 dark:text-yellow-100',
        sub: 'text-yellow-700 dark:text-yellow-300',
        divider: 'border-yellow-300 dark:border-yellow-700',
      };

  const Icon = isVerified ? CheckCircle2 : AlertCircle;
  const title = isVerified ? 'Verified Harvest Correction Detected' : 'Possible Harvest Correction Detected';
  const confidenceLabel = isVerified
    ? `Confirmed by ${confirmations.length} subsequent harvest-origin sample${confirmations.length === 1 ? '' : 's'} · High confidence`
    : confirmations.length > 0
    ? `1 confirming harvest sample · Moderate confidence — additional harvest measurements would validate`
    : `No subsequent harvest data yet · Moderate confidence — additional harvest measurements would validate`;

  const recommendedActions = isVerified
    ? [
        'Review historical sample methodology.',
        'Review camera calibration.',
        'Review sample size adequacy.',
        'Consider historical biomass estimates conservative.',
        'No stock adjustment recommended.',
      ]
    : [
        'Continue normal sampling.',
        'Seek confirmation through future harvest-derived weights.',
        'Review sample methodology.',
        'Do not interpret as missing fish without supporting evidence.',
      ];

  return (
    <div
      className={`border-2 ${palette.border} ${palette.bg} rounded-lg p-4 sm:p-5 shadow-sm`}
      data-testid={isVerified ? 'verified-harvest-correction-card' : 'possible-harvest-correction-card'}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-6 h-6 ${palette.sub} mt-0.5 flex-shrink-0`} />
        <div className="flex-1 space-y-3">
          <div>
            <h3 className={`text-base font-bold ${palette.accent}`}>{title}</h3>
            <p className={`text-xs ${palette.sub} mt-0.5`}>{confidenceLabel}</p>
          </div>

          <p className={`text-sm ${palette.accent} leading-relaxed`}>{signal.suggested_text}</p>

          <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t ${palette.divider}`}>
            <div data-testid="vhc-trigger-date">
              <p className={`text-[10px] uppercase tracking-wide ${palette.sub} font-semibold`}>Trigger Date</p>
              <p className={`text-sm font-bold ${palette.accent}`}>{t.date}</p>
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wide ${palette.sub} font-semibold`}>Prior → New</p>
              <p className={`text-sm font-bold ${palette.accent}`}>
                {Math.round(t.prior_weight_g)} g → {Math.round(t.new_weight_g)} g
              </p>
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wide ${palette.sub} font-semibold`}>Growth vs Expected</p>
              <p className={`text-sm font-bold ${palette.accent}`} data-testid="vhc-multiplier">
                {t.multiplier.toFixed(1)}× expected
              </p>
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wide ${palette.sub} font-semibold`}>vs Model</p>
              <p className={`text-sm font-bold ${palette.accent}`}>+{t.percent_above_model.toFixed(0)}%</p>
            </div>
          </div>

          {confirmations.length > 0 && (
            <div className={`pt-2 border-t ${palette.divider}`}>
              <p className={`text-[10px] uppercase tracking-wide ${palette.sub} font-semibold mb-1.5`}>
                Subsequent harvest confirmations
              </p>
              <ul className={`text-xs ${palette.accent} space-y-0.5`} data-testid="vhc-confirmations-list">
                {confirmations.map((c) => (
                  <li key={c.date}>
                    {c.date}: {Math.round(c.weight_g)} g vs new-trajectory {Math.round(c.expected_g)} g (
                    {c.pct_deviation >= 0 ? '+' : ''}
                    {c.pct_deviation.toFixed(1)}%)
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={`pt-2 border-t ${palette.divider}`}>
            <p className={`text-[10px] uppercase tracking-wide ${palette.sub} font-semibold mb-1.5`}>
              Recommended actions
            </p>
            <ul className={`text-xs ${palette.accent} space-y-0.5 list-disc list-inside`} data-testid="vhc-recommended-actions">
              {recommendedActions.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>

          <p className={`text-[11px] italic ${palette.sub} pt-1`}>
            Routine samples, camera samples, and standard manual samples continue to be evaluated by existing Jump Analysis and Missing Fish Detection logic.
          </p>
        </div>
      </div>
    </div>
  );
}
